import React, { useState, useRef, useEffect } from 'react';
import { Mp3Encoder } from '@breezystack/lamejs';
import { FaMicrophone, FaMicrophoneAltSlash } from "react-icons/fa";
import { Toast } from 'primereact/toast';
import { ThiTrucTuyenService } from 'services/ThiTrucTuyenService';

const AudioRecorder = (props) => {
    let { onStartRecore, finish, onAudioUploaded, questionId, previousAudioUrl, onSpeakingChange } = props;
    const [isRecording, setIsRecording] = useState(false);
    const [audioURL, setAudioURL] = useState('');
    const [mp3URL, setMp3URL] = useState('');
    const [isPlaying, setIsPlaying] = useState(false); // State để track trạng thái phát audio
    const mediaRecorderRef = useRef(null);
    const mediaStreamRef = useRef(null);
    const audioChunksRef = useRef([]);
    const audioRef = useRef(null); // Ref cho audio element
    const toast = useRef(null);

    // Voice activity detection (simple RMS-based, to drive UI waveform)
    const audioCtxRef = useRef(null);

    const analyserRef = useRef(null);
    const analyserBufRef = useRef(null);
    const rafRef = useRef(null);
    const lastVoiceTsRef = useRef(0);

    const stopVoiceDetection = () => {
        if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
        analyserRef.current = null;
        analyserBufRef.current = null;
        if (audioCtxRef.current) {
            try {
                audioCtxRef.current.close();
            } catch (e) {
                // ignore
            }
            audioCtxRef.current = null;
        }
        if (typeof onSpeakingChange === 'function') onSpeakingChange(false);
    };

    const startVoiceDetection = (stream) => {
        try {
            const AudioContextClass = window.AudioContext || window.webkitAudioContext;
            if (!AudioContextClass) return;

            const ctx = new AudioContextClass();
            audioCtxRef.current = ctx;

            const source = ctx.createMediaStreamSource(stream);
            const analyser = ctx.createAnalyser();
            analyser.fftSize = 1024;
            analyser.smoothingTimeConstant = 0.8;
            source.connect(analyser);

            analyserRef.current = analyser;
            analyserBufRef.current = new Uint8Array(analyser.fftSize);

            // Threshold + debounce
            const VOICE_RMS_THRESHOLD = 0.02;
            const SILENCE_HOLD_MS = 300;
            lastVoiceTsRef.current = 0;

            const tick = () => {
                const a = analyserRef.current;
                const buf = analyserBufRef.current;
                if (!a || !buf) return;

                a.getByteTimeDomainData(buf);

                let sum = 0;
                for (let i = 0; i < buf.length; i++) {
                    const v = (buf[i] - 128) / 128; // [-1..1]
                    sum += v * v;
                }
                const rms = Math.sqrt(sum / buf.length);

                const now = Date.now();
                if (rms >= VOICE_RMS_THRESHOLD) {
                    lastVoiceTsRef.current = now;
                }
                const speaking = now - lastVoiceTsRef.current <= SILENCE_HOLD_MS;
                if (typeof onSpeakingChange === 'function') onSpeakingChange(speaking);

                rafRef.current = requestAnimationFrame(tick);
            };

            rafRef.current = requestAnimationFrame(tick);
        } catch (e) {
            // ignore voice detection failures
        }
    };

    const startRecording = async () => {
        try {
            // Gọi callback khi bắt đầu ghi âm
            onStartRecore();

            // Yêu cầu quyền truy cập micro
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaStreamRef.current = stream;

            // Start voice detection for waveform UI
            startVoiceDetection(stream);

            // Kiểm tra định dạng MIME phù hợp (ưu tiên webm/ogg vì phổ biến hơn)
            const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
                ? 'audio/webm;codecs=opus'
                : MediaRecorder.isTypeSupported('audio/webm')
                    ? 'audio/webm'
                    : MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')
                        ? 'audio/ogg;codecs=opus'
                        : MediaRecorder.isTypeSupported('audio/mp4')
                            ? 'audio/mp4'
                            : ''; // Định dạng mặc định nếu không được hỗ trợ

            if (!mimeType) {
                throw new Error('Trình duyệt của bạn không hỗ trợ ghi âm với định dạng phù hợp.');
            }

            mediaRecorderRef.current = new MediaRecorder(stream, { mimeType });
            audioChunksRef.current = [];

            // Lưu dữ liệu khi ghi âm
            mediaRecorderRef.current.ondataavailable = (event) => {
                audioChunksRef.current.push(event.data);
            };

            // Xử lý khi dừng ghi âm
            mediaRecorderRef.current.onstop = async () => {
                if (audioChunksRef.current.length > 0) {
                    const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
                    const url = URL.createObjectURL(audioBlob);
                    setAudioURL(url);

                    // Convert sang MP3 và upload file lên MinIO
                    try {
                        // Convert audio sang MP3
                        const mp3Blob = await convertToMp3(audioBlob);

                        if (!mp3Blob) {
                            // Nếu convert thất bại, upload file gốc
                            console.warn('Convert to MP3 failed, uploading original file');
                            await uploadAudioFile(audioBlob, mimeType);
                        } else {
                            // Upload file MP3
                            await uploadAudioFile(mp3Blob, 'audio/mpeg', 'mp3');
                        }
                    } catch (error) {
                        console.error('Upload error:', error);
                        toast.current?.show({
                            severity: 'warn',
                            summary: 'Cảnh báo',
                            detail: 'Không thể upload file lên server. File chỉ được lưu tạm.',
                            life: 3000
                        });
                    }
                } else {
                    console.error("Không có dữ liệu âm thanh được ghi lại.");
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Có lỗi',
                        detail: 'Không có dữ liệu âm thanh nào được ghi lại.',
                        life: 3000
                    });
                }

                // Dừng stream để giải phóng tài nguyên
                stream.getTracks().forEach(track => track.stop());
            };

            // Xử lý lỗi từ MediaRecorder
            mediaRecorderRef.current.onerror = (e) => {
                console.error('Lỗi MediaRecorder:', e.error);
                toast.current?.show({
                    severity: 'error',
                    summary: 'Có lỗi',
                    detail: 'Lỗi khi ghi âm. Vui lòng thử lại.',
                    life: 3000
                });
            };

            // Bắt đầu ghi âm
            mediaRecorderRef.current.start();
            setIsRecording(true);
        } catch (error) {
            // Xử lý các lỗi cụ thể
            if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Có lỗi',
                    detail: 'Bạn đã từ chối quyền truy cập micrô.',
                    life: 3000
                });
            } else if (error.name === 'NotFoundError') {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Có lỗi',
                    detail: 'Thiết bị của bạn không có micro.',
                    life: 3000
                });
            } else if (error.message) {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Có lỗi',
                    detail: error.message,
                    life: 3000
                });
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Có lỗi',
                    detail: 'Đã xảy ra lỗi không xác định.',
                    life: 3000
                });
            }

            // Giải phóng tài nguyên nếu stream đã được tạo
            if (mediaStreamRef.current) {
                mediaStreamRef.current.getTracks().forEach(track => track.stop());
            }
            stopVoiceDetection();
        }
    };



    const stopRecording = () => {
        if (mediaRecorderRef.current) {
            mediaRecorderRef.current.stop();
        }

        if (mediaStreamRef.current) {
            const tracks = mediaStreamRef.current.getTracks();
            tracks.forEach(track => track.stop());
        }
        setIsRecording(false);
        stopVoiceDetection();
    };

    // Convert audio blob sang MP3 với nén tối ưu để giảm dung lượng
    const convertToMp3 = async (audioBlob) => {
        try {
            // Decode audio bằng Web Audio API
            const arrayBuffer = await audioBlob.arrayBuffer();
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

            // Giảm sample rate nếu quá cao (giọng nói chỉ cần 16kHz-22kHz)
            let sampleRate = audioBuffer.sampleRate;
            // Giữ nguyên sampleRate gốc để tránh hiện tượng méo tiếng do mismatch tốc độ mẫu

            // Force mono cho giọng nói (giảm 50% dung lượng so với stereo)
            const numChannels = 1; // Luôn dùng mono
            const length = audioBuffer.length;
            const originalChannels = audioBuffer.numberOfChannels;

            // Mix tất cả channels thành mono
            const mixedSamples = new Float32Array(length);
            if (originalChannels === 1) {
                // Đã là mono, chỉ cần copy
                const channelData = audioBuffer.getChannelData(0);
                for (let i = 0; i < length; i++) {
                    mixedSamples[i] = channelData[i];
                }
            } else {
                // Mix nhiều channels thành mono
                for (let i = 0; i < length; i++) {
                    let sum = 0;
                    for (let ch = 0; ch < originalChannels; ch++) {
                        sum += audioBuffer.getChannelData(ch)[i];
                    }
                    mixedSamples[i] = sum / originalChannels;
                }
            }

            // Convert Float32Array sang Int16Array (PCM)
            const int16Samples = new Int16Array(length);
            for (let i = 0; i < length; i++) {
                const s = Math.max(-1, Math.min(1, mixedSamples[i]));
                int16Samples[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
            }

            // Encode sang MP3 với bitrate 128 kbps (chất lượng cao cho giọng nói)
            const mp3Encoder = new Mp3Encoder(numChannels, sampleRate, 128); // 128 kbps
            const mp3Data = [];
            const sampleBlockSize = 1152;

            for (let i = 0; i < int16Samples.length; i += sampleBlockSize) {
                const sampleChunk = int16Samples.subarray(i, i + sampleBlockSize);
                const mp3buf = mp3Encoder.encodeBuffer(sampleChunk);
                if (mp3buf.length > 0) {
                    mp3Data.push(mp3buf);
                }
            }

            // Flush encoder
            const endBuf = mp3Encoder.flush();
            if (endBuf.length > 0) {
                mp3Data.push(endBuf);
            }

            // Tạo MP3 Blob
            const mp3Blob = new Blob(mp3Data, { type: 'audio/mpeg' });
            return mp3Blob;
        } catch (error) {
            console.error('Convert to MP3 error:', error);
            return null;
        }
    };

    // Extract key từ MinIO URL để xóa file
    const extractKeyFromUrl = (url) => {
        if (!url) return null;
        try {

            const urlObj = new URL(url);
            const pathParts = urlObj.pathname.split('/').filter(p => p);

            // Tìm index của bucket name 'exam'
            const bucketIndex = pathParts.findIndex(p => p === 'exam');
            if (bucketIndex >= 0 && bucketIndex < pathParts.length - 1) {
                // Lấy phần sau bucket name
                return pathParts.slice(bucketIndex + 1).join('/');
            }

            // Fallback: nếu không tìm thấy, lấy tất cả sau bucket (giả sử bucket là phần tử đầu)
            if (pathParts.length > 1) {
                return pathParts.slice(1).join('/');
            }

            return null;
        } catch (e) {
            // Nếu URL không hợp lệ (relative path), thử extract từ string pattern
            // Pattern: /exam/exam/answers/... hoặc exam/answers/...
            const patterns = [
                /\/exam\/exam\/(.+)$/,  // /exam/exam/answers/...
                /\/exam\/(.+)$/,         // /exam/answers/...
                /exam\/(.+)$/            // exam/answers/...
            ];

            for (const pattern of patterns) {
                const match = url.match(pattern);
                if (match && match[1]) {
                    return match[1];
                }
            }

            // Nếu không match pattern nào, có thể URL đã là key
            if (url.startsWith('exam/')) {
                return url;
            }

            return null;
        }
    };

    // Xóa file cũ trên MinIO
    const deleteOldFile = async (oldUrl) => {
        if (!oldUrl) return;
        try {
            const key = extractKeyFromUrl(oldUrl);
            if (key) {
                await ThiTrucTuyenService.deleteFile('exam', key);
                console.log('Deleted old file:', key);
            }
        } catch (error) {
            console.warn('Failed to delete old file:', error);
            // Không throw error, chỉ log warning
        }
    };

    // Xóa tất cả files trong thư mục của câu hỏi này
    const deleteAllFilesInFolder = async (prefix) => {
        try {
            // Đảm bảo prefix có dấu / ở cuối để list đúng các files trong thư mục
            const folderPrefix = prefix.endsWith('/') ? prefix : prefix + '/';

            // List tất cả files trong thư mục
            const listResult = await ThiTrucTuyenService.listFiles('exam', folderPrefix, 100);

            if (listResult.data && listResult.data.status && listResult.data.data) {
                const data = listResult.data.data;
                const objects = data.objects || [];

                if (objects.length > 0) {
                    // Xóa từng file
                    for (const obj of objects) {
                        if (obj.key) {
                            try {
                                await ThiTrucTuyenService.deleteFile('exam', obj.key);
                                console.log('Deleted old file:', obj.key);
                            } catch (error) {
                                console.warn('Failed to delete file:', obj.key, error);
                            }
                        }
                    }

                    console.log(`Deleted ${objects.length} old file(s) in folder: ${folderPrefix}`);
                } else {
                    console.log(`No files found in folder: ${folderPrefix}`);
                }
            }
        } catch (error) {
            console.warn('Failed to list/delete files in folder:', error);
            // Không throw error, chỉ log warning để không ảnh hưởng đến upload
        }
    };

    // Upload audio file lên MinIO
    const uploadAudioFile = async (audioBlob, mimeType, extension = null) => {
        try {
            const userId = localStorage.getItem('User_Id');
            const attemptCode = localStorage.getItem('exam_attempt_code') || 'temp';

            // Kiểm tra questionId
            if (!questionId) {
                throw new Error('questionId is required');
            }

            // Prefix mới: exam/answers/{userId}/{attemptCode}/{id_question}/
            const prefix = `exam/answers/${userId}/${attemptCode}/${questionId}`;

            // Xóa tất cả files cũ trong thư mục này trước khi upload file mới (ĐÃ TẮT THEO YÊU CẦU)
            // await deleteAllFilesInFolder(prefix);

            // Nếu có previousAudioUrl, cũng thử xóa (backup method) (ĐÃ TẮT THEO YÊU CẦU)
            // if (previousAudioUrl) {
            //     await deleteOldFile(previousAudioUrl);
            // }

            // Xác định extension
            let fileExtension = extension;
            if (!fileExtension) {
                if (mimeType.includes('mp4')) {
                    fileExtension = 'm4a';
                } else if (mimeType.includes('ogg')) {
                    fileExtension = 'ogg';
                } else if (mimeType.includes('webm')) {
                    fileExtension = 'webm';
                } else {
                    fileExtension = 'mp3'; // Mặc định MP3 sau khi convert
                }
            }

            const fileName = `recording_${Date.now()}.${fileExtension}`;
            const audioFile = new File([audioBlob], fileName, { type: mimeType });

            const uploadResult = await ThiTrucTuyenService.uploadFile(audioFile, 'exam', prefix);

            if (uploadResult.data && uploadResult.data.status && uploadResult.data.data) {
                const minioUrl = uploadResult.data.data.url;
                // Gọi callback với URL từ MinIO
                if (onAudioUploaded) {
                    onAudioUploaded(minioUrl);
                }
            } else {
                console.error('Upload failed:', uploadResult.data);
                toast.current?.show({
                    severity: 'warn',
                    summary: 'Cảnh báo',
                    detail: 'Không thể upload file lên server. File chỉ được lưu tạm.',
                    life: 3000
                });
            }
        } catch (error) {
            console.error('Upload error:', error);
            throw error;
        }
    };

    // Reset audioURL khi component mount lại (khi ghi lại)
    useEffect(() => {
        // Reset state khi component mount
        setAudioURL('');
        setIsRecording(false);
        // Cleanup: giải phóng media stream nếu có
        if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach(track => track.stop());
            mediaStreamRef.current = null;
        }
        // Cleanup: dừng media recorder nếu có
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            try {
                mediaRecorderRef.current.stop();
            } catch (e) {
                // Ignore error if already stopped
            }
            mediaRecorderRef.current = null;
        }
        audioChunksRef.current = [];
        stopVoiceDetection();

        return () => {
            stopVoiceDetection();
        };
    }, []); // Chạy khi component mount

    useEffect(() => {
        if (finish) {
            stopRecording();
        }
        else {
            startRecording();
        }
    }, [finish]);

    const toggleAudio = async () => {
        try {
            const el = audioRef.current;
            if (!el) return;
            if (isPlaying) {
                el.pause();
                setIsPlaying(false);
            } else {
                await el.play();
                setIsPlaying(true);
            }
        } catch (e) {
            // ignore play error
        }
    };

    // Sync state khi audio play/pause
    useEffect(() => {
        const audioEl = audioRef.current;
        if (!audioEl) return;

        const handlePlay = () => setIsPlaying(true);
        const handlePause = () => setIsPlaying(false);
        const handleEnded = () => setIsPlaying(false);

        audioEl.addEventListener('play', handlePlay);
        audioEl.addEventListener('pause', handlePause);
        audioEl.addEventListener('ended', handleEnded);

        return () => {
            audioEl.removeEventListener('play', handlePlay);
            audioEl.removeEventListener('pause', handlePause);
            audioEl.removeEventListener('ended', handleEnded);
        };
    }, [audioURL]); // Re-run khi audioURL thay đổi

    return (
        <div>
            <Toast ref={toast} />

            {/* {audioURL == '' &&
                <h3>Ghi âm câu trả lời</h3>
            } */}

            {/* {audioURL && (
                <div style={{ marginTop: '20px', marginBottom: '20px', width: '100%' }}>
                    <h3>Âm Thanh Ghi Lại:</h3>
                    <audio
                        controlsList="nodownload"
                        key={audioURL}
                        ref={audioRef}
                        style={{ display: 'none' }}
                        onEnded={() => setIsPlaying(false)}
                    >
                        <source src={audioURL} type="audio/ogg" />
                        <source src={audioURL} type="audio/mpeg" />
                    </audio>
                    <button
                        type="button"
                        className="btn-audio-toggle"
                        onClick={toggleAudio}
                    >
                        <span className="icon">{isPlaying ? '⏸' : '⏵'}</span>
                        <span className="text">
                            <u>{isPlaying ? 'Stop' : 'Play'}</u>
                        </span>
                    </button>
                </div>
            )} */}

        </div>
    );
};

export default AudioRecorder;
